<template>
  <div :class="[
    type === 'textarea' ? 'zm-textarea' : 'zm-input',
    inputSize ? 'zm-input--' + inputSize : '',
    {
      'is-disabled': inputDisabled,
      'zm-input-group': $slots.prepend || $slots.append,
      'zm-input-group--append': $slots.append,
      'zm-input-group--prepend': $slots.prepend,
      'zm-input--prefix': $slots.prefix || prefixIcon,
      'zm-input--suffix': $slots.suffix || suffixIcon || clearable || showPassword
    }
    ]"
       @mouseenter="hovering = true"
       @mouseleave="hovering = false"
  >
    <template v-if="type !== 'textarea'">
      <!-- 前置元素 -->
      <div class="zm-input-group__prepend" v-if="$slots.prepend">
        <slot name="prepend"></slot>
      </div>
      <input
        v-if="type !== 'textarea'"
        class="zm-input__inner"
        v-bind="$attrs"
        :type="showPassword ? (passwordVisible ? 'text': 'password') : type"
        :disabled="inputDisabled"
        :readonly="readonly"
        :autocomplete="autocomplete"
        ref="input"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @keyup.enter="handleSubmit"
        :aria-label="label"
        :aria-labelledby="labelBy"
        :aria-invalid="isShowErrorMessage"
      >
      <!-- 前置内容 -->
      <span class="zm-input__prefix" v-if="$slots.prefix || prefixIcon">
        <slot name="prefix"></slot>
        <i class="zm-input__icon"
           v-if="prefixIcon"
           :class="prefixIcon">
        </i>
      </span>
      <!-- 后置内容 -->
      <span
        class="zm-input__suffix"
        v-if="$slots.suffix || suffixIcon || showClear|| showPassword || validateState && needStatusIcon">
        <span class="zm-input__suffix-inner">
          <template v-if="!showClear || !showPwdVisible">
            <slot name="suffix"></slot>
            <i class="zm-input__icon"
               v-if="suffixIcon"
               :class="suffixIcon">
            </i>
          </template>
          <i v-if="showClear"
             class="zm-input__icon zm-icon-close zm-input__clear"
             @click="clear"
          ></i>
          <i v-if="showPwdVisible"
             class="zm-input__icon zm-icon-view zm-input__clear"
             @click="handlePasswordVisible"
          ></i>
        </span>
        <i class="zm-input__icon"
           v-if="validateState"
           :class="['zm-input__validateIcon', validateIcon]">
        </i>
      </span>
      <!-- 后置元素 -->
      <div class="zm-input-group__append" v-if="$slots.append">
        <slot name="append"></slot>
      </div>
    </template>
    <textarea
      v-else
      class="zm-textarea__inner"
      @compositionstart="handleCompositionStart"
      @compositionend="handleCompositionEnd"
      @input="handleInput"
      ref="textarea"
      v-bind="$attrs"
      :disabled="inputDisabled"
      :readonly="readonly"
      :autocomplete="autocomplete"
      :style="textareaStyle"
      @focus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
      @keyup.enter="handleSubmit"
      :aria-label="label"
      :aria-labelledby="labelBy"
      :aria-invalid="isShowErrorMessage"
    >
    </textarea>
  </div>
</template>
<script>
  import emitter from '../../mixins/emitter';
  import calcTextareaHeight from './calcTextareaHeight';
  import merge from '../../utils/merge';

  export default {
    name: 'ZmInput',

    componentName: 'ZmInput',

    mixins: [emitter],

    inheritAttrs: false,

    inject: {
      ZmForm: {
        default: ''
      },
      ZmFormItem: {
        default: ''
      }
    },

    data() {
      return {
        textareaCalcStyle: {},
        hovering: false,
        focused: false,
        isComposing: false,
        passwordVisible: false
      };
    },

    props: {
      value: [String, Number],
      size: {
        type: String,
        default: 'small'
      },
      resize: String,
      disabled: Boolean,
      readonly: Boolean,
      type: {
        type: String,
        default: 'text'
      },
      autosize: {
        type: [Boolean, Object],
        default: false
      },
      autocomplete: {
        type: String,
        default: 'off'
      },
      validateEvent: {
        type: Boolean,
        default: true
      },
      suffixIcon: String,
      prefixIcon: String,
      label: String,
      labelBy: String,
      clearable: {
        type: Boolean,
        default: false
      },
      showPassword: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      _elFormItemSize() {
        return (this.ZmFormItem || {}).elFormItemSize;
      },
      validateState() {
        return this.ZmFormItem ? this.ZmFormItem.validateState : '';
      },
      isShowErrorMessage() {
        return this.ZmFormItem && this.ZmFormItem.isShowErrorMessage || false;
      },
      needStatusIcon() {
        return this.ZmForm ? this.ZmForm.statusIcon : false;
      },
      validateIcon() {
        return {
          validating: 'zm-icon-loading',
          success: 'zm-icon-success',
          error: 'zm-icon-error'
        }[this.validateState];
      },
      textareaStyle() {
        return merge({}, this.textareaCalcStyle, { resize: this.resize });
      },
      inputSize() {
        return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
      },
      inputDisabled() {
        return this.disabled || (this.ZmForm || {}).disabled;
      },
      nativeInputValue() {
        return this.value === null || this.value === undefined ? '' : String(this.value);
      },
      showClear() {
        return this.clearable &&
          !this.inputDisabled &&
          !this.readonly &&
          this.nativeInputValue &&
          (this.focused || this.hovering);
      },
      showPwdVisible() {
        return this.showPassword &&
          !this.inputDisabled &&
          !this.readonly &&
          (!!this.nativeInputValue || this.focused);
      }
    },

    watch: {
      value(val) {
        this.$nextTick(this.resizeTextarea);
        if (this.validateEvent) {
          this.dispatch('ZmFormItem', 'el.form.change', [val]);
        }
      },
      // native input value is set explicitly
      // do not use v-model / :value in template
      // see: https://github.com/ElemeFE/element/issues/14521
      nativeInputValue() {
        this.setNativeInputValue();
      },
      // when change between <input> and <textarea>,
      // update DOM dependent value and styles
      // https://github.com/ElemeFE/element/issues/14857
      type() {
        this.$nextTick(() => {
          this.setNativeInputValue();
          this.resizeTextarea();
          this.updateIconOffset();
        });
      }
    },

    methods: {
      focus() {
        this.getInput().focus();
      },
      blur() {
        this.getInput().blur();
      },
      handleBlur(event) {
        this.focused = false;
        this.$emit('blur', event);
        if (this.validateEvent) {
          this.dispatch('ZmFormItem', 'el.form.blur', [this.value]);
        }
      },
      select() {
        this.getInput().select();
      },
      resizeTextarea() {
        if (this.$isServer) return;
        const { autosize, type } = this;
        if (type !== 'textarea') return;
        if (!autosize) {
          this.textareaCalcStyle = {
            minHeight: calcTextareaHeight(this.$refs.textarea).minHeight
          };
          return;
        }
        const minRows = autosize.minRows;
        const maxRows = autosize.maxRows;

        this.textareaCalcStyle = calcTextareaHeight(this.$refs.textarea, minRows, maxRows);
      },
      setNativeInputValue() {
        const input = this.getInput();
        if (!input) return;
        if (input.value === this.nativeInputValue) return;
        input.value = this.nativeInputValue;
      },
      handleFocus(event) {
        this.focused = true;
        this.$emit('focus', event);
      },
      handleCompositionStart() {
        this.isComposing = true;
      },
      handleCompositionEnd(event) {
        this.isComposing = false;
        this.handleInput(event);
      },
      handleInput(event) {
        // should not emit input during composition
        // see: https://github.com/ElemeFE/element/issues/10516
        if (this.isComposing) return;

        // hack for https://github.com/ElemeFE/element/issues/8548
        // should remove the following line when we don't support IE
        if (event.target.value === this.nativeInputValue) return;

        this.$emit('input', event.target.value);

        // ensure native input value is controlled
        // see: https://github.com/ElemeFE/element/issues/12850
        this.$nextTick(this.setNativeInputValue);
      },
      handleChange(event) {
        this.$emit('change', event.target.value);
      },
      handleSubmit(event) {
        this.$emit('submit', event.target.value);
      },
      calcIconOffset(place) {
        let elList = [].slice.call(this.$el.querySelectorAll(`.zm-input__${place}`) || []);
        if (!elList.length) return;
        let el = null;
        for (let i = 0; i < elList.length; i++) {
          if (elList[i].parentNode === this.$el) {
            el = elList[i];
            break;
          }
        }
        if (!el) return;
        const pendantMap = {
          suffix: 'append',
          prefix: 'prepend'
        };

        const pendant = pendantMap[place];
        if (this.$slots[pendant]) {
          el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${this.$el.querySelector(`.zm-input-group__${pendant}`).offsetWidth}px)`;
        } else {
          el.removeAttribute('style');
        }
      },
      updateIconOffset() {
        this.calcIconOffset('prefix');
        this.calcIconOffset('suffix');
      },
      clear() {
        this.$emit('input', '');
        this.$emit('change', '');
        this.$emit('clear');
      },
      handlePasswordVisible() {
        this.passwordVisible = !this.passwordVisible;
        this.focus();
      },
      getInput() {
        return this.$refs.input || this.$refs.textarea;
      }
    },

    created() {
      this.$on('inputSelect', this.select);
    },

    mounted() {
      this.setNativeInputValue();
      this.resizeTextarea();
      this.updateIconOffset();
    },

    updated() {
      this.$nextTick(this.updateIconOffset);
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(textarea) {
    display: inline-block;
    width: 100%;
    vertical-align: bottom;

    @include e(inner) {
      display: block;
      resize: vertical;
      padding: 4px $--input-default-padding-horizontal;
      line-height: 22px;
      box-sizing: border-box;
      width: 100%;
      font-size: inherit;
      color: $--input-color;
      background-color: $--input-fill;
      background-image: none;
      border: 1px solid $--input-border-color;
      border-radius: $--input-border-radius;
      transition: $--border-transition-base;

      &::placeholder {
        color: $--input-placeholder-color;
      }

      &:hover {
        border-color: $--input-focus-border;
      }

      &:focus {
        outline: none;
        border-color: $--input-focus-border;
      }
    }

    @include when(disabled) {
      .zm-textarea__inner {
        background-color: $--input-disabled-fill;
        border-color: $--input-disabled-border-color;
        color: $--input-disabled-color;
        cursor: not-allowed;

        &::placeholder {
          color: $--input-disabled-placeholder-color;
        }
      }
    }
  }

  @include b(input) {
    position: relative;
    font-size: inherit;
    display: inline-block;
    width: 100%;
    @include scroll-bar;

    & .zm-input__clear {
      color: $--input-icon-color;
      cursor: pointer;
      transition: $--color-transition-base;

      &:hover {
        color: $--input-clear-hover-color;
      }
    }

    @include e(inner) {
      -webkit-appearance: none;
      background-color: $--input-fill;
      background-image: none;
      border-radius: $--input-border-radius;
      border: 1px solid $--input-border-color;
      box-sizing: border-box;
      color: $--input-color;
      display: inline-block;
      font-size: inherit;
      line-height: $--input-lg-height;
      height: $--input-lg-height;
      outline: none;
      padding: $--input-default-padding-vertical $--input-default-padding-horizontal;
      transition: $--border-transition-base;
      width: 100%;

      &[type="number"] {
        padding-right: 0;
      }

      &::placeholder {
        color: $--input-placeholder-color;
      }

      &:hover {
        border-color: $--input-focus-border;
      }

      &:focus {
        outline: none;
        border-color: $--input-focus-border;
      }
    }

    @include e(suffix) {
      position: absolute;
      height: 100%;
      right: 5px;
      top: 0;
      text-align: center;
      color: $--input-icon-color;
      transition: all .3s;
      pointer-events: none;
      line-height: $--input-lg-height;
    }

    @include e(suffix-inner) {
      pointer-events: all;
    }

    @include e(prefix) {
      position: absolute;
      height: 100%;
      left: 5px;
      top: 0;
      text-align: center;
      color: $--input-icon-color;
      transition: all .3s;
      line-height: $--input-lg-height;
    }

    @include e(icon) {
      height: 100%;
      width: 25px;
      text-align: center;
      transition: all .3s;

      &:after {
        content: '';
        height: 100%;
        width: 0;
        display: inline-block;
        vertical-align: middle;
      }
    }

    @include e(validateIcon) {
      pointer-events: none;
    }

    @include when(active) {
      .zm-input__inner {
        outline: none;
        border-color: $--input-focus-border;
      }
    }

    @include when(disabled) {
      .zm-input__inner {
        background-color: $--input-disabled-fill;
        border-color: $--input-disabled-border-color;
        color: $--input-disabled-color;
        cursor: not-allowed;

        &::placeholder {
          color: $--input-disabled-placeholder-color;
        }
      }

      .zm-input__icon {
        cursor: not-allowed;
      }
    }

    @include m(large) {
      @include e(inner) {
        line-height: $--input-lg-height;
      }
    }
    @include m(small) {
      .zm-input__suffix, .zm-input__prefix {
        line-height: $--input-height;
      }
      @include e(inner) {
        height: $--input-height;
        line-height: $--input-height;
      }
    }

    @include m(suffix) {
      .zm-input__inner {
        padding-right: 30px;
      }
    }

    @include m(prefix) {
      .zm-input__inner {
        padding-left: 30px;
      }
    }

  }

  @include b(input-group) {
    line-height: normal;
    display: inline-table;
    width: 100%;
    border-collapse: separate;

    > .zm-input__inner {
      vertical-align: middle;
      display: table-cell;
    }

    @include e((append, prepend)) {
      background-color: $--background-color-base;
      color: $--color-info;
      vertical-align: middle;
      display: table-cell;
      position: relative;
      border: $--border-base;
      border-radius: $--input-border-radius;
      padding: 0 20px;
      width: 1px;
      white-space: nowrap;

      &:focus {
        outline: none;
      }

      .zm-select,
      .zm-button {
        display: inline-block;
        margin: -10px -20px;
      }

      button.zm-button,
      div.zm-select .zm-input__inner,
      div.zm-select:hover .zm-input__inner {
        border-color: transparent;
        background-color: transparent;
        color: inherit;
        border-top: 0;
        border-bottom: 0;
      }

      .zm-button,
      .zm-input {
        font-size: inherit;
      }
    }

    @include e(prepend) {
      .zm-select {
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        display: block;
        .zm-select-input {
          min-width: 110px;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          .zm-button {
            right: 25px;
            margin-top: -8px;
          }
        }
      }
    }

    @include e(append) {
      border-left: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    @include m(prepend) {
      .zm-input__inner {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
      .zm-select .zm-input.is-focus .zm-input__inner {
        border-color: transparent;
      }
    }

    @include m(append) {
      .zm-input__inner {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
      .zm-select .zm-input.is-focus .zm-input__inner {
        border-color: transparent;
      }
    }
    &.input-with-select {
      .zm-input-group__prepend {
        border-left: 0;
      }
    }
  }

  /** disalbe default clear on IE */
  .zm-input__inner::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
</style>
